import { useGetProvidersSuspense } from "@/orval/gen"
import { Button, Divider, Stack } from "@mantine/core"
import { useTranslation } from "react-i18next"

interface Props {
  tenantId?: string
  onLogin: (providerId: number) => void
}

export default function SsoLoginButtons({ tenantId, onLogin }: Props) {
  const { data: providers } = useGetProvidersSuspense({ tenantId })
  const { t } = useTranslation("app_server")

  if (providers.length === 0) return null

  return (
    <Stack gap={"xs"} w="100%">
      <Divider label={t("Login_SsoLogin")} labelPosition="center" />
      {providers.map((provider) => {
        const providerId = provider.id
        const authority = provider.authority
        return providerId === undefined || !authority ? null : (
          <ReachableProviderButton
            key={`${providerId}:${authority}`}
            authority={authority}
            name={provider.name}
            providerId={providerId}
            onLogin={onLogin}
          />
        )
      })}
    </Stack>
  )
}

interface ReachableProviderButtonProps {
  authority: string
  name?: string
  providerId: number
  onLogin: (providerId: number) => void
}

function ReachableProviderButton({
  authority,
  name,
  providerId,
  onLogin,
}: ReachableProviderButtonProps) {
  const { t } = useTranslation("app_server")
  const reachable = useIsUrlReachable(authority)

  if (!reachable) return null

  return (
    <Button
      type="button"
      w="100%"
      variant="outline"
      style={{ borderColor: "var(--mantine-color-gray-4)" }}
      c={"var(--mantine-color-text)"}
      onClick={() => onLogin(providerId)}
    >
      {t("LoginWithProvider", { provider: name })}
    </Button>
  )
}

function useIsUrlReachable(url: string) {
  const [reachable, setReachable] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false

    setReachable(false)
    void checkURL(url).then((isReachable) => {
      if (!cancelled) {
        setReachable(isReachable)
      }
    })

    return () => {
      cancelled = true
    }
  }, [url])

  return reachable
}

async function checkURL(url: string) {
  try {
    await fetch(url, {
      mode: "no-cors",
      signal: AbortSignal.timeout(5000),
    })
    return true
  } catch {
    return false
  }
}
